import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, CircularProgress, Alert, Select, MenuItem, Chip,
    FormControl, InputLabel, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAllUsers,  } from '../api/admin';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
}));

const VAndSControl = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ role: 'all', admin_verification_status: 'all' });
    const [editUser, setEditUser] = useState(null);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await getAllUsers();
            setUsers(data.users || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch users.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleOpenEditDialog = (user) => {
        setEditUser({ ...user });
    };

    const handleCloseEditDialog = () => {
        setEditUser(null);
    };

    const handleSaveVerification = async () => {
        if (!editUser) return;

        try {
            const token = localStorage.getItem('jwtToken');
            const { id, role, admin_verification_status, admin_verification_notes } = editUser;

            const response = await axios.patch(
                `${API_BASE_URL}/api/admin/users/${role}/${id}/verification`,
                { admin_verification_status, admin_verification_notes },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const updatedUser = response.data;
            setUsers(users.map(u => (u.id === id && u.role === role) ? updatedUser : u));
            handleCloseEditDialog();
        } catch (err) {
            setError('Failed to update verification status.');
            console.error(err);
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const roleMatch = filters.role === 'all' || user.role === filters.role;
            const statusMatch = filters.admin_verification_status === 'all' || user.admin_verification_status === filters.admin_verification_status;
            return roleMatch && statusMatch;
        });
    }, [users, filters]);

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <StyledPaper>
               
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Role</InputLabel>
                            <Select name="role" value={filters.role} label="Role" onChange={handleFilterChange}>
                                <MenuItem value="broker">Broker</MenuItem> 
                                <MenuItem value="all">All Roles</MenuItem>
                                <MenuItem value="shipper">Shipper</MenuItem>
                                <MenuItem value="carrier">Carrier</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Verification Status</InputLabel>
                            <Select name="admin_verification_status" value={filters.admin_verification_status} label="Verification Status" onChange={handleFilterChange}>
                                <MenuItem value="all">All Statuses</MenuItem>
                                <MenuItem value="unverified">Unverified</MenuItem>
                                <MenuItem value="verified">Verified</MenuItem>
                                
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </StyledPaper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#11224d' }}>
                            <TableCell sx={{ color: 'white' }}>Company</TableCell>
                            <TableCell sx={{ color: 'white' }}>Email</TableCell>
                            <TableCell sx={{ color: 'white' }}>Role</TableCell>
                            <TableCell sx={{ color: 'white' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map(user => (
                            <TableRow key={`${user.role}-${user.id}`}>
                                <TableCell>{user.company_name}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell sx={{ textTransform: 'capitalize' }}>
                                    <Chip
                                        label={user.role}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: '20px',
                                            backgroundColor:
                                                user.role === 'shipper' ? '#c8e6c9' : 
                                                user.role === 'carrier' ? '#fff9c4' : 
                                                user.role === 'broker'  ? '#bbdefb' : '#e0e0e0',
                                            color:
                                                user.role === 'shipper' ? '#1b5e20' : 
                                                user.role === 'carrier' ? '#f57f17' : 
                                                user.role === 'broker'  ? '#0d47a1' : '#000000', 
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: user.admin_verification_status === 'verified' ? 'green' :
                                                user.admin_verification_status === 'rejected' ? 'red' :
                                                user.admin_verification_status === 'pending' ? 'orange' : 'grey',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {user.admin_verification_status || 'unverified'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <ActionButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleOpenEditDialog(user)}
                                    >
                                        Manage
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            
            <Dialog open={!!editUser} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Manage Verification for {editUser?.company_name}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Verification Status</InputLabel>
                                <Select
                                    value={editUser?.admin_verification_status || 'unverified'}
                                    label="Verification Status"
                                    onChange={(e) => setEditUser({ ...editUser, admin_verification_status: e.target.value })}
                                >
                                    <MenuItem value="unverified">Unverified</MenuItem>
                                    <MenuItem value="verified">Verified</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {(editUser?.role === 'carrier' || editUser?.role === 'broker') && (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1 }}><strong>FMCSA-Email:</strong> {editUser?.fmcsa_email || 'N/A'}</Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Internal Verification Notes"
                                value={editUser?.admin_verification_notes || ''}
                                onChange={(e) => setEditUser({ ...editUser, admin_verification_notes: e.target.value })}
                                helperText="These notes are for admin use and will be visible to the user."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button onClick={handleSaveVerification} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VAndSControl;